import request from '../../utils/request';
import { Result } from '../../models/general';
import type {
    OffboardingProcessView,
    OffboardingProcessInput,
    OffboardingProcessTaskView,
    OffboardingProcessTaskInput,
    UpdateTaskStatusInput,
    EmployeeSelectionView,
    TerminateDirectInput,
    TerminateDirectResultView,
    ReverseTerminationInput,
    ReverseTerminationResultView,
} from '../../models/employee';

export async function getOffboardingProcesses(): Promise<Result<OffboardingProcessView[]>> {
    const response = await request.get<Result<OffboardingProcessView[]>>('OffboardingProcess/GetAll');
    return response.data;
}

export async function getOffboardingProcess(id: number): Promise<Result<OffboardingProcessView>> {
    const response = await request.get<Result<OffboardingProcessView>>(`OffboardingProcess/GetById/${id}`);
    return response.data;
}

export async function startOffboarding(input: OffboardingProcessInput): Promise<Result<OffboardingProcessView>> {
    const response = await request.post<Result<OffboardingProcessView>>('OffboardingProcess/StartOffboarding', input);
    return response.data;
}

// Fast-terminate: no template / no checklist. Sets status straight to Terminated/Resigned and runs EOS once.
export async function terminateDirect(input: TerminateDirectInput): Promise<Result<TerminateDirectResultView>> {
    const response = await request.post<Result<TerminateDirectResultView>>('OffboardingProcess/TerminateDirect', input);
    return response.data;
}

// Reverse a completed termination/resignation (A4): returns the employee to active, unwinds EOS + settlement.
export async function reverseTermination(input: ReverseTerminationInput): Promise<Result<ReverseTerminationResultView>> {
    const response = await request.post<Result<ReverseTerminationResultView>>('OffboardingProcess/ReverseTermination', input);
    return response.data;
}

export async function updateOffboarding(id: number, input: OffboardingProcessInput): Promise<Result<OffboardingProcessView>> {
    const response = await request.put<Result<OffboardingProcessView>>(`OffboardingProcess/UpdateOffboarding/${id}`, input);
    return response.data;
}

export async function cancelOffboarding(id: number): Promise<Result<boolean>> {
    const response = await request.post<Result<boolean>>(`OffboardingProcess/CancelOffboarding/${id}`);
    return response.data;
}

export async function deleteOffboarding(id: number): Promise<Result<boolean>> {
    const response = await request.delete<Result<boolean>>(`OffboardingProcess/DeleteOffboarding/${id}`);
    return response.data;
}

export async function updateOffboardingTaskStatus(taskId: number, input: UpdateTaskStatusInput): Promise<Result<OffboardingProcessTaskView>> {
    const response = await request.put<Result<OffboardingProcessTaskView>>(`OffboardingProcess/UpdateTaskStatus/${taskId}`, input);
    return response.data;
}

export async function addOffboardingTask(processId: number, input: OffboardingProcessTaskInput): Promise<Result<OffboardingProcessTaskView>> {
    const response = await request.post<Result<OffboardingProcessTaskView>>(`OffboardingProcess/AddTask/${processId}`, input);
    return response.data;
}

export async function removeOffboardingTask(taskId: number): Promise<Result<boolean>> {
    const response = await request.delete<Result<boolean>>(`OffboardingProcess/RemoveTask/${taskId}`);
    return response.data;
}

export async function getOffboardingEmployeeSelections(): Promise<Result<EmployeeSelectionView[]>> {
    const response = await request.get<Result<EmployeeSelectionView[]>>('OffboardingProcess/GetEmployeeSelections');
    return response.data;
}

export async function calculateEos(id: number): Promise<Result<OffboardingProcessView>> {
    const response = await request.post<Result<OffboardingProcessView>>(`OffboardingProcess/CalculateEos/${id}`);
    return response.data;
}
